import type { LanguageLocale } from 'utils/language'
import type { StoreFrontResponse } from 'mobx/AddressManager/types'

export interface Configuration {
	orderTypeFlows: OrderTypeFlow[]
	canSkipOrderTypeSelection: boolean
	displayToMenuButton: boolean
  savedAddressesEnabled?: boolean
	enabled: boolean
}

export interface Localization {
	actualFlow: LocalizationFlow | null | undefined
	processingFlow: LocalizationFlow | null | undefined

	orderTypeFlows: OrderTypeFlow[]
	setOrderTypeFlows: (orderTypeFlows: OrderTypeFlow[]) => void

	matchStoreForCurrentAddress: (continueFlowpWhenNoStoreMatched?: boolean) => Promise<void>

	savedAddresses: SavedAddress[]
	savedAddressesFetched: boolean
	fetchSavedAddresses: () => Promise<void>
	clearSavedAddresses: () => void
	saveNewAddress: (address: NonSavedAddress, label: AddressLabel, customLabel?: string) => Promise<SavedAddress | undefined>
	saveCurrentAddress: () => Promise<SavedAddress>
	removeSavedAddress: (addressId: string) => Promise<void>
	reachedMaximumSavedAddresses: boolean

	started: boolean | undefined
	currentStep: FlowStep | null
	alreadyLocalized: boolean | undefined

	start: () => void
	abort: () => void
	end: () => void
	nextStep: () => void
	previousStep: () => void
	goToStep: (step: FlowStep) => void
	canGoToStep: (step: FlowStep) => boolean

	getValidateAddress(address: NonSavedAddress): Promise<any> | undefined
	goToLocalizedMenu: () => void
	getLocalizedMenuPath: () => string | undefined

	config: () => Configuration | undefined
  savedAddressesEnabled: boolean

	skipOrderTypeSelection: () => Promise<boolean> | undefined

	forceLocalizationToStore: (storeId: string) => void
}

export class LocalizationFlow {
	constructor() {}

	orderTypeFlow: OrderTypeFlow | null = null

	setOrderTypeFlow = (orderTypeFlow: OrderTypeFlow) => {
		this.orderTypeFlow = orderTypeFlow
	}

	setOrderTypeFlowDisplayAvailableStores = (displayAvailableStores: boolean) => {
		if (this.orderTypeFlow) {
			this.orderTypeFlow = {
				...this.orderTypeFlow,
				displayAvailableStores,
			}
		}
	}

	store: Store | null = null

	setStore = (store: Store) => {
		this.store = store
	}

	address: NonSavedAddress | null = null

	setAddress = (address: NonSavedAddress) => {
		this.address = address
	}

	clearAddress = () => {
		this.address = null
		this.savedAddressId = null
	}

	setAddressLocation = (formatted: string, location: Location) => {
		if (this.address) {
			this.address = {
				...this.address,
				formatted,
				location,
			}
		}
	}

	savedAddressId: string | null = null

	get usingSavedAddress() {
		return !!this.savedAddressId
	}

	selectSavedAddress = ({ _id, formatted, location, components }: SavedAddress) => {
		this.address = { formatted, location, components }
		this.savedAddressId = _id
	}

	unselectSavedAddress = () => {
		this.savedAddressId = null
	}

	setSavedAddressId = (addressId: string | null) => {
		this.savedAddressId = addressId
	}
}

interface GetStoresResponse {
	shouldGoToFirstStoreFound: boolean
	stores: any[]
}

export interface LocalizationDependencies {
	getStores: () => Promise<{ deliveryStores: boolean; disablePickup: boolean; pickupStores: [] }>
	getAllStores: () => Promise<GetStoresResponse>
	getStoresOnAddress: (address: NonSavedAddress) => Promise<GetStoresResponse>

	clearCouponToApply: () => void

	// Once we move the stores to be on the localizationStore, it should be there
	updateMapStores: (stores: any[]) => void

	fetchSavedAddresses: () => Promise<SavedAddress[]>
	saveNewAddress: (address: NonSavedAddress, label: AddressLabel, customLabel?: string) => Promise<SavedAddress>
	updateSavedAddress: (address: SavedAddress) => Promise<void>
	deleteSavedAddress: (id: string) => Promise<void>

	save(flow: LocalizationFlow): void
	get(): LocalizationFlow | null
	config(): Configuration | undefined

	initSessionAndLoadMenu(storeId: string): Promise<boolean>
	initSession(storeId?: string): Promise<string>

	getValidateAddress(address: NonSavedAddress): Promise<any>

	goToLocalizedMenu: () => Promise<void>
	getLocalizedMenuPath: () => string | undefined

	getStoreDetailsById(storeId: string): Promise<StoreFrontResponse>
	checkPickupAvailabilityByStore(store: StoreFrontResponse): void

	sendLocalizationStartEvent(): void
	sendLocalizationFinishEvent(storeId: string, orderType: string): void

	emitAddressSavedEvent(savedAddress: SavedAddress): void
	emitAddressFailedToSaveEvent(error: unknown): void
}

export interface OrderTypeFlow {
	orderType: OrderType
	orderTypeTranslationKey: string
	orderTypeImageSrc: string
	steps: FlowStep[]
	requireAddress: boolean
	displayAvailableStores?: boolean
}

export interface Store {
	id: string
	name: Partial<Record<LanguageLocale, string>>
	address: string
}

export enum FlowStep {
	OrderTypeSelection = 'OrderTypeSelection',
	AddressSelection = 'AddressSelection',
	AddressDetailsSelection = 'AddressDetailsSelection',
	StoreOnMapSelection = 'StoreOnMapSelection',
}

export interface NonSavedAddress {
	formatted: string
	location: Location
	components: google.maps.GeocoderAddressComponent[]
}

export interface BaseSavedAddress extends NonSavedAddress {
	_id: string
	label: AddressLabel
}

export type SavedAddress = HomeSavedAddress | WorkSavedAddress | OtherSavedAddress

export interface HomeSavedAddress extends BaseSavedAddress {
	label: AddressLabel.Home
}

export interface WorkSavedAddress extends BaseSavedAddress {
	label: AddressLabel.Work
}

export interface OtherSavedAddress extends BaseSavedAddress {
	label: AddressLabel.Other
	customLabel: string
}

export interface Location {
	lat: number
	lng: number
}

export enum OrderType {
	Delivery = 'delivery',
	Pickup = 'pickup',
}

export enum AddressLabel {
	Home = 'home',
	Work = 'work',
	Other = 'other',
}

export interface SavingAddressData {
	label: AddressLabel
	customLabel?: string
}

/*
interface Field {
    label: string
    translatedKey: string
}

const addressDetailsFields : AddressDetails[] = [
    {
        fields : [
            {label: 'Floor', translatedKey: 'xxx'},
            {label: 'Apt number', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    },
    {
        fields : [
            {label: 'Floor', translatedKey: 'xxx'},
            {label: 'Company name', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    },
    {
        fields : [
            {label: 'Doorbell', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    },
    {
        fields : [
            {label: 'Type of location', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    }

]
*/

/*

type AddressDetailsTypes = {
    [key in AddressType]?: Field[]
}

// Need to check if this should be saved in database, or hardcoded
const addressDetailsFields : AddressDetailsTypes = {
    [AddressType.Apartment]: [
        {label: 'Floor', translatedKey: 'xxx'},
        {label: 'Apt number', translatedKey: 'xxx'},
        {label: 'Notes to delivery person', translatedKey: 'xxx'}
    ] as Field[],
    [AddressType.Office]: [
        {label: 'Floor', translatedKey: 'xxx'},
        {label: 'Company name', translatedKey: 'xxx'},
        {label: 'Notes to delivery person', translatedKey: 'xxx'}
    ] as Field[],
    [AddressType.House]: [
        {label: 'Doorbell', translatedKey: 'xxx'},
        {label: 'Notes to delivery person', translatedKey: 'xxx'}
    ] as Field[],
    [AddressType.Others]: [
        {label: 'Type of location', translatedKey: 'xxx'},
        {label: 'Notes to delivery person', translatedKey: 'xxx'}
    ] as Field[],
}
*/

/*
type OrderTypesFlows = {
    [key in OrderType]?: {
        steps: FlowStep[],
        requireAddress: boolean
    }
}

const addressDetailsFields2 : AddressDetails[] = [
    {
        type: AddressType.Apartment,
        fields : [
            {label: 'Floor', translatedKey: 'xxx'},
            {label: 'Apt number', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    },
    {
        type: AddressType.Office,
        fields : [
            {label: 'Floor', translatedKey: 'xxx'},
            {label: 'Company name', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    },
    {
        type: AddressType.House,
        fields : [
            {label: 'Doorbell', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    },
    {
        type: AddressType.Others,
        fields : [
            {label: 'Type of location', translatedKey: 'xxx'},
            {label: 'Notes to delivery person', translatedKey: 'xxx'}
        ]
    }

] */
